// Vendors — every supplier, decorator and service vendor in one list, with a
// way to add one (pilot 2026-10-13: vendors had no emails, decorators no
// addresses, and there was no screen to add or edit them; the admin vendor
// console only lists catalogue suppliers).
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { PageHeader } from "@/components/ui/PageHeader";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { cn } from "@/lib/utils";

export interface VendorDirectoryRow {
  id: string;
  name: string;
  source: string;
  primaryEmail: string | null;
  /** The art department's email (CC'd on POs) — Stephanie 2026-10-16. */
  artEmail?: string | null;
  primaryPhone: string | null;
  isDecorator: boolean;
  city: string | null;
  stateCode: string | null;
}

type Filter = "all" | "decorators" | "no_email";
const FILTERS: Array<[Filter, string]> = [
  ["all", "All"],
  ["decorators", "Decorators"],
  ["no_email", "No order email"],
];

export default function VendorsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAdd = user?.roles.some((r) => r === "admin" || r === "production_rep" || r === "client_rep") ?? false;
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newArtEmail, setNewArtEmail] = useState("");
  const [newDecorator, setNewDecorator] = useState(false);

  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["vendors", "directory"],
    queryFn: () => api<{ rows: VendorDirectoryRow[] }>("/vendors/directory"),
  });

  const create = useMutation({
    mutationFn: () =>
      api<{ vendor: { id: string } }>("/vendors", {
        method: "POST",
        body: JSON.stringify({
          name: newName.trim(),
          ...(newEmail.trim() ? { primary_email: newEmail.trim() } : {}),
          ...(newArtEmail.trim() ? { art_email: newArtEmail.trim() } : {}),
          is_decorator: newDecorator,
        }),
      }),
    onSuccess: (res) => {
      // The directory and every supplier picker show the new one (Stephanie 2026-11-02 sweep).
      void qc.invalidateQueries({ queryKey: ["vendors"] });
      navigate(`/production/vendors/${res.vendor.id}`);
    },
  });

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (data?.rows ?? []).filter((r) => {
      if (filter === "decorators" && !r.isDecorator) return false;
      if (filter === "no_email" && r.primaryEmail) return false;
      if (!term) return true;
      return [r.name, r.primaryEmail, r.artEmail, r.city, r.stateCode].some((v) => v?.toLowerCase().includes(term));
    });
  }, [data, q, filter]);

  const counts = useMemo(() => {
    const all = data?.rows ?? [];
    return {
      all: all.length,
      decorators: all.filter((r) => r.isDecorator).length,
      no_email: all.filter((r) => !r.primaryEmail).length,
    } satisfies Record<Filter, number>;
  }, [data]);

  return (
    <div className="mx-auto w-full">
      <PageHeader
        eyebrow="Production / Vendors"
        title="Vendors"
        intro="Suppliers, decorators and service vendors — the order email a PO goes to, where goods ship, and who to call."
        action={
          canAdd && !adding ? (
            <Button onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" /> New vendor
            </Button>
          ) : undefined
        }
      />

      {adding && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (newName.trim()) create.mutate();
          }}
          className="mb-5 flex flex-wrap items-end gap-3 rounded-md border border-rule bg-white p-4"
        >
          <label className="flex min-w-[220px] flex-1 flex-col gap-1">
            <span className="text-xs font-medium text-muted">Name</span>
            <Input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={200} placeholder="e.g. Universal Screen Printing" />
          </label>
          <label className="flex min-w-[220px] flex-1 flex-col gap-1">
            <span className="text-xs font-medium text-muted">Order email (optional)</span>
            <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="orders@vendor.com" />
          </label>
          <label className="flex min-w-[220px] flex-1 flex-col gap-1">
            <span className="text-xs font-medium text-muted">Art email (optional)</span>
            <Input type="email" value={newArtEmail} onChange={(e) => setNewArtEmail(e.target.value)} placeholder="art@vendor.com" />
          </label>
          <label className="flex h-10 items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={newDecorator} onChange={(e) => setNewDecorator(e.target.checked)} className="h-4 w-4" />
            Decorator
          </label>
          <div className="flex gap-2">
            <Button type="submit" disabled={!newName.trim() || create.isPending}>
              {create.isPending ? "Adding…" : "Add vendor"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
          {create.error && (
            <p className="basis-full text-sm text-risk">{apiErrorMessage(create.error, "Couldn't add the vendor.")}</p>
          )}
        </form>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, city…" className="pl-9" aria-label="Search vendors" />
        </div>
        <div className="flex overflow-hidden rounded-md border border-rule bg-white" role="group" aria-label="Filter vendors">
          {FILTERS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={cn(
                "px-3 py-2 text-sm",
                filter === key ? "bg-ink text-white" : "text-ink hover:bg-cream-100",
              )}
            >
              {label} <span className="tabular-nums opacity-70">{counts[key]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-md border border-rule bg-white">
        {isLoading ? (
          <p className="px-5 py-10 text-center text-sm text-muted">Loading vendors…</p>
        ) : error ? (
          <p className="px-5 py-10 text-center text-sm text-risk">Couldn't load vendors.</p>
        ) : rows.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-muted">No vendors match.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
              <tr>
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Order email</th>
                <th className="hidden px-4 py-2.5 lg:table-cell">Art email</th>
                <th className="hidden px-4 py-2.5 md:table-cell">Phone</th>
                <th className="hidden px-4 py-2.5 sm:table-cell">Location</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#efefec]">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-cream-50">
                  <td className="px-4 py-2.5">
                    <Link to={`/production/vendors/${r.id}`} className="font-medium text-ink hover:underline">
                      {r.name}
                    </Link>
                    {r.isDecorator && (
                      <span className="ml-2 rounded bg-cream-100 px-1.5 py-0.5 text-[11px] text-muted">Decorator</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {r.primaryEmail ? (
                      <span className="text-ink">{r.primaryEmail}</span>
                    ) : (
                      <span className="text-risk">Missing — POs can't be emailed</span>
                    )}
                  </td>
                  <td className="hidden px-4 py-2.5 lg:table-cell">
                    {r.artEmail ? <span className="text-ink">{r.artEmail}</span> : <span className="text-muted">—</span>}
                  </td>
                  <td className="hidden px-4 py-2.5 text-muted md:table-cell">{r.primaryPhone ?? "—"}</td>
                  <td className="hidden px-4 py-2.5 text-muted sm:table-cell">
                    {r.city || r.stateCode ? [r.city, r.stateCode].filter(Boolean).join(", ") : r.isDecorator ? <span className="text-risk">No address</span> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
